package co.laiautomation.downtyme

import com.laiautomation.kdxp.KdxpAdminExperience
import com.laiautomation.kdxp.KdxpApplicationProvisioningConfig
import com.laiautomation.kdxp.KdxpApplicationProvisioningTemplate
import com.laiautomation.kdxp.KdxpCapabilities
import com.laiautomation.kdxp.KdxpConfig
import com.laiautomation.kdxp.KdxpFeatureDefaults
import com.laiautomation.kdxp.KdxpFeaturePresentation
import com.laiautomation.kdxp.KdxpVocabulary
import com.laiautomation.kdxp.appmodel.KdxpNavigationPlacement
import com.laiautomation.kdxp.backend.KdxpWorkspaceMembershipMode

/**
 * Creates the KDXP consumer configuration used by every Downtyme target.
 * Public deployment values are generated at build time and remain overridable for host-specific launches.
 */
fun downtymeConfig(
    baseUrl: String = DowntymeBuildConfig.baseUrl,
    clientId: String = DowntymeBuildConfig.clientId,
): KdxpConfig = KdxpConfig(
    baseUrl = baseUrl,
    clientId = clientId,
    adminClientId = clientId,
    applicationName = "Downtyme",
    applicationSubtitle = "Campaign management for any tabletop RPG",
    vocabulary = KdxpVocabulary(
        siteSingular = "Campaign",
        sitePlural = "Campaigns",
        siteOwnerSingular = "Game Manager",
        siteOwnerPlural = "Game Managers",
        siteMemberSingular = "Player",
        siteMemberPlural = "Players",
        documentSingular = "Handout",
        documentPlural = "Handouts",
        objectSingular = "Campaign data",
        objectPlural = "Campaign data",
        join = "Join campaign",
        requestAccess = "Request to join",
        leave = "Leave campaign",
        customizeApp = "Customize campaign",
        setup = "Campaign setup",
    ),
    features = KdxpFeatureDefaults(
        home = KdxpFeaturePresentation(
            label = "Campaign",
            iconKey = "house",
            placement = KdxpNavigationPlacement.PRIMARY,
            order = 0,
        ),
        sites = KdxpFeaturePresentation(
            label = "Discover",
            iconKey = "search",
            placement = KdxpNavigationPlacement.PRIMARY,
            order = 10,
        ),
        documents = KdxpFeaturePresentation(
            label = "Handouts",
            iconKey = "folder-open",
            placement = KdxpNavigationPlacement.PRIMARY,
            order = 20,
        ),
        objects = KdxpFeaturePresentation(
            label = "Campaign Data",
            iconKey = "database",
            placement = KdxpNavigationPlacement.PRIMARY,
            order = 30,
        ),
        users = KdxpFeaturePresentation(
            label = "Players",
            iconKey = "users",
            placement = KdxpNavigationPlacement.MORE,
            order = 40,
        ),
        builder = KdxpFeaturePresentation(
            label = "Customize campaign",
            iconKey = "settings",
            placement = KdxpNavigationPlacement.MORE,
            order = 90,
        ),
        setup = KdxpFeaturePresentation(
            label = "Campaign setup",
            iconKey = "settings",
            placement = KdxpNavigationPlacement.MORE,
            order = 100,
        ),
    ),
    adminExperience = KdxpAdminExperience.SIMPLE,
    capabilities = KdxpCapabilities(
        allowSchemaEditing = false,
        allowCompanyScopedObjects = false,
        allowSiteScopedObjects = true,
        showTechnicalIdentifiers = false,
        allowUserAdministration = true,
        allowRoleAdministration = false,
        allowBuilderAccess = true,
        allowSetupAccess = true,
        allowUploads = true,
        allowSiteSwitcher = true,
        allowSiteDiscovery = true,
        allowMembershipRequests = true,
    ),
    objectFolderExternalReferenceCode = "DOWNTYME",
    applicationProvisioning = KdxpApplicationProvisioningConfig(
        enabled = true,
        createDefaultApplicationOnPlatformSetup = true,
        defaultApplicationDisplayName = "My Campaign",
        defaultApplicationKey = "downtyme-default",
        templates = listOf(
            KdxpApplicationProvisioningTemplate(
                id = "generic",
                displayName = "Generic campaign",
                description = "A system-agnostic tabletop campaign.",
                membershipMode = KdxpWorkspaceMembershipMode.RESTRICTED,
                snapshotFactory = { appKey, displayName ->
                    downtymeAppSnapshot(appKey, displayName)
                },
            ),
        ),
        defaultTemplateId = "generic",
        allowCustomKey = true,
        allowUserApplicationCreation = true,
    ),
)
